import * as React from "react"
import { FaInstagram, FaUser } from "react-icons/fa"
import { SiTiktok } from "react-icons/si"
import { gql } from "@apollo/client"
import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Spinner,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import Head from "next/head"
import { useRouter } from "next/router"

import { PrivacySetting, StatsResponse, useGetProfileStatsQuery, useGetUserQuery } from "lib/graphql"
import { useMe } from "lib/hooks/useMe"
import { ConnectionsModalContent } from "components/ConnectionsModalContent"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { LinkButton } from "components/LinkButton"
import { Modal } from "components/Modal"
import { ProfileActs } from "components/ProfileActs"
import { ProfileBanner } from "components/ProfileBanner"
import { ProfileEvents } from "components/ProfileEvents"
import { ProfilePosts } from "components/ProfilePosts"
import { ProfileWelcomeModal } from "components/ProfileWelcomeModal"
import { UserConnectActions } from "components/UserConnectActions"

const _ = gql`
  fragment UserProfile on User {
    id
    firstName
    lastName
    fullName
    pronounsMulti
    pronounsOther
    bio
    avatarUrl
    interests
    privacySetting
    isConnected
    connectionStatus
    connections {
      ...UserConnection
    }
    connectionProjects {
      items {
        id
      }
    }
    tiktok
    instagram
    connectionsCount
    recommendedPosts {
      items {
        ...PostItem
      }
    }
    connectionProjects {
      items {
        ...LatestProjectItem
      }
    }
    recommendedConnections {
      ...RecommendedConnectionItem
    }
    recommendedEvents {
      ...EventItem
    }
    recommendedTutorials {
      ...TutorialItem
    }
    agenda {
      ...AgendaItem
    }
  }

  query GetUser($id: String!) {
    user(id: $id) {
      ...UserProfile
    }
  }

  query GetProfileStats($userId: String!) {
    profileStats(userId: $userId) {
      events
      competitions
      tutorials
      posts
      projects
    }
  }
`

export default function UserDetail() {
  const router = useRouter()
  const id = router.query.id as string

  const { me } = useMe()
  const connectionModalProps = useDisclosure()

  const { data, loading } = useGetUserQuery({ variables: { id }, skip: !!!id })
  const user = data?.user

  const isCurrentUser = me?.id === user?.id

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Spinner />
      </Center>
    )
  if (!user) return <Center>Gebruiker niet gevonden</Center>

  return (
    <>
      <Head>
        <title>
          Kunstbende - {user.firstName} {user.lastName}
        </title>
      </Head>
      {isCurrentUser && <ProfileBanner />}
      <Limiter mt={{ base: 12, md: 24 }} mb={56}>
        <Stack spacing={8} aria-label={`Profiel pagina van ${user.fullName}`}>
          <Flex justify="space-between" direction={{ base: "column", lg: "row" }} mb={8}>
            <Flex direction={{ base: "column", md: "row" }} align={{ base: "center", md: "flex-start" }}>
              <Avatar
                borderColor="black"
                borderWidth={3}
                src={user.avatarUrl || undefined}
                name={user.fullName || "avatar"}
                w="120px"
                h="120px"
                mb={2}
                mr={{ base: 0, md: 10 }}
                bg="gray.600"
                icon={<FaUser color="gray.900" size={32} />}
                aria-label={`${user.fullName || "Gebruiker"} profielfoto`}
              />
              <Stack align={{ base: "center", md: "flex-start" }} marginTop={{ base: 10, md: 0 }}>
                <Flex align="center">
                  <Heading fontSize={{ base: 24, md: 28 }} mr={4} fontWeight={900}>
                    {user.firstName} {user.lastName}
                  </Heading>
                  {isCurrentUser && me ? (
                    <>
                      <LinkButton href={`/users/${me.id}/edit`} size="xs" variant="outline">
                        Profiel bewerken
                        {me.profileIncomplete && (
                          <Box
                            pos="absolute"
                            top={-1}
                            right={-1}
                            bg="red.500"
                            borderRadius="full"
                            boxSize="10px"
                          />
                        )}
                      </LinkButton>

                      {isCurrentUser && <ProfileWelcomeModal />}
                    </>
                  ) : (
                    <UserConnectActions user={user} />
                  )}
                </Flex>
                {(user.pronounsMulti || user.pronounsOther) && (
                  <Text mt={0} fontSize="sm" textAlign={{ base: "center", md: "left" }}>
                    {user.pronounsMulti
                      .filter((pronoun) => pronoun !== "Anders, namelijk")
                      .map(
                        (pronoun, index) =>
                          `${pronoun}${user.pronounsMulti.length - 1 !== index ? ", " : ""}`,
                      )}

                    {user.pronounsOther}
                  </Text>
                )}
                <Text fontSize="lg">
                  {user.interests.map(
                    (interest, index) => `${interest}${user.interests.length - 1 !== index ? " / " : ""}`,
                  )}
                </Text>
                <Button variant="unstyled" onClick={connectionModalProps.onOpen}>
                  {user.connectionsCount}{" "}
                  <Text as="span" fontWeight="normal">
                    connections
                  </Text>
                </Button>
                {user.bio && (
                  <Text textAlign={{ base: "center", md: "left" }} w={{ base: "90%", md: "70%" }}>
                    {user.bio}
                  </Text>
                )}
              </Stack>
            </Flex>
            <Stack spacing={8}>
              {user && <ProfileStats userId={user.id} />}
              {isCurrentUser ||
              user.privacySetting === PrivacySetting.Public ||
              (user.privacySetting === PrivacySetting.Connections && user.isConnected) ? (
                <HStack>
                  {user.tiktok && (
                    <Link href={`https://www.tiktok.com/@${user.tiktok}`} isExternal aria-label="TikTok">
                      <Icon as={SiTiktok} boxSize="24px" />
                    </Link>
                  )}
                  {user.instagram && (
                    <Link href={`https://www.instagram.com/${user.instagram}`} isExternal aria-label="Instagram">
                      <Icon as={FaInstagram} boxSize="24px" />
                    </Link>
                  )}
                </HStack>
              ) : null}
            </Stack>
          </Flex>
          {isCurrentUser ||
          user.privacySetting === PrivacySetting.Public ||
          (user.privacySetting === PrivacySetting.Connections && user.isConnected) ? (
            <Tabs defaultIndex={0} isLazy>
              <TabList
                justifyContent="center"
                overflow="auto"
                css={{ "&::-webkit-scrollbar": { display: "none" } }}
                w={{ base: "95vw", md: "auto" }}
                pt={2} pb={2}
              >
                <HStack w="100%" justify={{ base: "flex-start", md: "center" }} spacing={{ base: 4, md: 0 }}>
                  <Tab
                    fontWeight="black"
                    color="gray.500"
                    fontSize={{ base: "sm", sm: "sm", md: "lg" }}
                    paddingInlineStart={{ base: 1, sm: 2, md: 4 }}
                    paddingInlineEnd={{ base: 1, sm: 2, md: 4 }}
                    _selected={{ color: "white", borderColor: "yellow.500", borderBottomWidth: 4 }}
                    textTransform="uppercase"
                    position="relative"
                  >
                    WEDSTRIJD
                    {me?.hasPendingFeedback && (
                      <Box
                        pos="absolute"
                        top={2}
                        right={2}
                        bg="yellow.500"
                        borderRadius="full"
                        boxSize="10px"
                      />
                    )}
                  </Tab>
                  <Tab
                    fontWeight="black"
                    color="gray.500"
                    fontSize={{ base: "sm", sm: "sm", md: "lg" }}
                    paddingInlineStart={{ base: 1, sm: 2, md: 4 }}
                    paddingInlineEnd={{ base: 1, sm: 2, md: 4 }}
                    _selected={{ color: "white", borderColor: "yellow.500", borderBottomWidth: 4 }}
                    textTransform="uppercase"
                    whiteSpace="nowrap"
                  >
                    AGENDA
                  </Tab>
                  <Tab
                    fontWeight="black"
                    color="gray.500"
                    fontSize={{ base: "sm", sm: "sm", md: "lg" }}
                    paddingInlineStart={{ base: 1, sm: 2, md: 4 }}
                    paddingInlineEnd={{ base: 1, sm: 2, md: 4 }}
                    _selected={{ color: "white", borderColor: "yellow.500", borderBottomWidth: 4 }}
                    textTransform="uppercase"
                  >
                    PRIKBORD
                  </Tab>
                </HStack>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <ProfileActs userId={user.id} />
                </TabPanel>
                <TabPanel>
                  <ProfileEvents userId={user.id} />
                </TabPanel>
                <TabPanel>
                  <ProfilePosts userId={user.id} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <Stack spacing={8} align="center">
              <Divider />
              <Text pt={8} color="gray.400">
                Privé profiel
              </Text>
              <Button size="sm" variant="outline" colorScheme="gray">
                Connect
              </Button>
            </Stack>
          )}
        </Stack>
        <Modal title="Connections" {...connectionModalProps} size="2xl">
          <ConnectionsModalContent connections={user.connections} userId={user.id} />
        </Modal>
      </Limiter>
    </>
  )
}

UserDetail.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>

interface Props {
  userId: string
}

function ProfileStats({ userId }: Props) {
  const { data: statsData } = useGetProfileStatsQuery({
    variables: { userId: userId },
    skip: !!!userId,
  })

  const stats = statsData?.profileStats

  const highestStats: Partial<StatsResponse> = stats
    ? Object.entries(stats)
        .filter((stat) => stat[1] !== "StatsResponse")
        .sort(([, a]: [any, number], [, b]: [any, number]) => b - a)
        .slice(0, 4)
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})
    : {}

  return (
    <HStack
      align="flex-start"
      spacing={{ base: 0, lg: 10 }}
      marginTop={{ base: 10, lg: 0 }}
      bg="gray.700"
      justifyContent="space-between"
      py={4}
      px={{ base: 2, md: 8 }}
      borderRadius="xl"
    >
      {(highestStats.competitions as number) >= 0 && (
        <Stat display="flex" justifyContent={{ base: "center", lg: "flex-start" }}>
          <StatNumber fontSize={36} color="yellow.500" fontWeight="black">
            {stats?.competitions}
          </StatNumber>
          <StatLabel fontSize={{ base: "xs", md: "md" }} fontWeight="black">
            Wedstrijd
          </StatLabel>
          <StatHelpText fontSize={{ base: "xs", md: "md" }}>bijgewoond</StatHelpText>
        </Stat>
      )}
      {(highestStats.events as number) >= 0 && (
        <Stat display="flex" justifyContent={{ base: "center", lg: "flex-start" }}>
          <StatNumber fontSize={36} color="yellow.500" fontWeight="black">
            {stats?.events}
          </StatNumber>
          <StatLabel fontSize={{ base: "xs", md: "md" }} fontWeight="black">
            Agenda
          </StatLabel>
          <StatHelpText fontSize={{ base: "xs", md: "md" }}>bijgewoond</StatHelpText>
        </Stat>
      )}
      {(highestStats.projects as number) >= 0 && (
        <Stat display="flex" justifyContent={{ base: "center", lg: "flex-start" }}>
          <StatNumber fontSize={36} color="yellow.500" fontWeight="black">
            {stats?.projects}
          </StatNumber>
          <StatLabel fontSize={{ base: "xs", md: "md" }} fontWeight="black">
            Projecten
          </StatLabel>
          <StatHelpText fontSize={{ base: "xs", md: "md" }}>gemaakt</StatHelpText>
        </Stat>
      )}
      {(highestStats.tutorials as number) >= 0 && (
        <Stat display="flex" justifyContent={{ base: "center", lg: "flex-start" }}>
          <StatNumber fontSize={36} color="yellow.500" fontWeight="black">
            {stats?.tutorials}
          </StatNumber>
          <StatLabel fontSize={{ base: "xs", md: "md" }} fontWeight="black">
            Tutorials
          </StatLabel>
          <StatHelpText fontSize={{ base: "xs", md: "md" }}>voltooid</StatHelpText>
        </Stat>
      )}
      {(highestStats.posts as number) >= 0 && (
        <Stat display="flex" justifyContent={{ base: "center", lg: "flex-start" }}>
          <StatNumber fontSize={36} color="yellow.500" fontWeight="black">
            {stats?.posts}
          </StatNumber>
          <StatLabel fontSize={{ base: "xs", md: "md" }} fontWeight="black">
            Berichten
          </StatLabel>
          <StatHelpText fontSize={{ base: "xs", md: "md" }}>gedeeld</StatHelpText>
        </Stat>
      )}
    </HStack>
  )
}
